import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';

const HowItWorks = () => {
  const [activeStep, setActiveStep] = useState(null);
  
  const steps = [
    { 
      number: '1', 
      title: 'Create Your Card', 
      description: 'Design your digital business card in the AM CARDMATE app with custom colors, logos, and links to your social profiles and website.',
      icon: 'palette',
      color: 'from-blue-400 to-blue-600',
      image: '/api/placeholder/600/400'
    },
    { 
      number: '2', 
      title: 'Share Digitally', 
      description: 'Share your digital profile via QR code, email, text, or social media links. Recipients can view your card on any device without downloading an app.',
      icon: 'share-alt',
      color: 'from-indigo-400 to-purple-600',
      image: '/api/placeholder/600/400'
    },
    { 
      number: '3', 
      title: 'Get NFC Card', 
      description: 'Enhance your networking with a physical NFC card through subscription or direct purchase. Choose from plastic, wooden, or app style plastic materials.',
      icon: 'credit-card',
      color: 'from-purple-400 to-pink-600',
      image: '/api/placeholder/600/400'
    },
    { 
      number: '4', 
      title: 'Tap to Share', 
      description: 'Share your digital profile with a simple tap on any smartphone. Your NFC card automatically directs people to your latest digital card.',
      icon: 'bolt',
      color: 'from-pink-400 to-red-600',
      image: '/api/placeholder/600/400'
    }
  ];

  return (
    <section id="how-it-works" className="py-24 bg-light relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(77,162,255,0.08)_0%,transparent_50%),radial-gradient(circle_at_70%_30%,rgba(222,97,97,0.08)_0%,transparent_50%)]"></div>
      
      {/* Background Blobs */}
      <motion.div 
        className="absolute top-20 left-20 w-64 h-64 rounded-full bg-blue-500/5 filter blur-3xl"
        animate={{ 
          scale: [1, 1.2, 1],
          x: [0, 30, 0],
          y: [0, -20, 0],
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />
      
      <motion.div 
        className="absolute bottom-20 right-20 w-64 h-64 rounded-full bg-pink-500/5 filter blur-3xl"
        animate={{ 
          scale: [1, 1.3, 1],
          x: [0, -20, 0],
          y: [0, 30, 0],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge badge-pink inline-flex mb-4">
            <FontAwesomeIcon icon="magic" />
            <span>Simple Process</span>
          </div>
          
          <h2 className="text-h2 mb-6">
            How <span className="text-gradient-blue-pink">AM CARDMATE</span> Works
          </h2>
          
          <p className="text-body-lg text-gray-600 mb-8">
            Create digital business cards in our app and enhance your networking with NFC cards that connect to your digital profile. The process is simple and seamless.
          </p>
        </div>
        
        {/* Desktop Steps */}
        <div className="hidden lg:block relative mb-20">
          <div className="flex justify-between relative">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className="w-1/4 px-4 text-center"
                onMouseEnter={() => setActiveStep(index)}
                onMouseLeave={() => setActiveStep(null)}
              >
                <div
                  className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center text-3xl text-white relative
                    bg-gradient-to-r ${step.color} shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300`}
                >
                  <FontAwesomeIcon icon={step.icon} />
                  
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white text-primary-blue text-body-sm flex items-center justify-center font-bold shadow-md">
                    {step.number}
                  </div>
                </div>
                
                <h3 className={`text-h5 mb-3 transition-colors duration-300 ${
                  activeStep === index ? 'text-primary-blue' : 'text-dark'
                }`}>
                  {step.title}
                </h3>
                
                <p className={`text-body-sm transition-all duration-300 ${
                  activeStep === index ? 'text-dark scale-105' : 'text-gray-600'
                }`}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Mobile Steps */}
        <div className="lg:hidden grid grid-cols-1 gap-8 mb-16">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className="bg-white rounded-xl p-6 shadow-md relative overflow-hidden border border-transparent hover:border-blue-100/30 hover:-translate-y-2 hover:shadow-lg transition-all duration-300"
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-400 to-pink-400"></div>
              
              <div className="flex gap-5">
                <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center text-white text-2xl relative flex-shrink-0`}>
                  <FontAwesomeIcon icon={step.icon} />
                  <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-white text-primary-blue text-caption flex items-center justify-center font-bold shadow">
                    {step.number}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-card-title text-dark mb-2">{step.title}</h3>
                  <p className="text-card-body">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* CTA Section */}
        <div className="mt-16 lg:mt-24 relative z-10 text-center">
          <div className="inline-flex items-center gap-4 bg-white rounded-full py-3 px-6 shadow-lg mb-8 hover:shadow-xl transition-all duration-300">
            <FontAwesomeIcon icon="lightbulb" className="text-primary-blue text-lg" />
            <span className="text-body font-semibold text-gray-dark">Simple, fast, and effective!</span>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="#app-download" 
              className="btn btn-primary hover:shadow-lg transition-all duration-300"
            >
              <FontAwesomeIcon icon="download" />
              <span>Get Started Today</span>
            </a>
            
            <Link 
              to="/nfc-cards" 
              className="btn btn-outline hover:shadow-lg transition-all duration-300"
            >
              <FontAwesomeIcon icon="info-circle" />
              <span>View NFC Card Options</span>
            </Link>
          </div>
          
          {/* Decorative Blobs */}
          <motion.div 
            className="absolute -top-10 -left-16 w-32 h-32 rounded-full bg-gradient-blue opacity-10 blur-2xl -z-1"
            animate={{ 
              x: [0, 20, 0],
              y: [0, -10, 0],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          
          <motion.div 
            className="absolute -bottom-10 -right-16 w-40 h-40 rounded-full bg-gradient-pink opacity-10 blur-2xl -z-1"
            animate={{ 
              x: [0, -20, 0],
              y: [0, 10, 0],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;